import { createContext, useContext, useEffect, useState } from 'react'
import { getTimeBasedTheme } from '@/lib/utils'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  actualTheme: 'dark' | 'light'
}

const initialState: ThemeProviderState = {
  theme: 'dark',
  setTheme: () => null,
  actualTheme: 'dark',
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  storageKey = 'westcoast-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  const [actualTheme, setActualTheme] = useState<'dark' | 'light'>('dark')

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    let resolvedTheme: 'dark' | 'light'

    if (theme === 'system') {
      // Use time-based theme
      resolvedTheme = getTimeBasedTheme()
      
      // Update theme based on time every minute
      const interval = setInterval(() => {
        const newTheme = getTimeBasedTheme()
        if (newTheme !== resolvedTheme) {
          resolvedTheme = newTheme
          setActualTheme(resolvedTheme)
          root.classList.remove('light', 'dark')
          root.classList.add(resolvedTheme)
        }
      }, 60000) // Check every minute

      // Cleanup interval
      return () => clearInterval(interval)
    } else {
      resolvedTheme = theme
    }

    setActualTheme(resolvedTheme)
    root.classList.add(resolvedTheme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
    actualTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}